import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLocalizedPath } from '../hooks/useLocalizedPath';

/**
 * Footer component with association information and links
 * (Requirements 4.4)
 */
const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useTranslation(['common', 'navigation']);
  const localizedPath = useLocalizedPath();

  return (
    <footer className="bg-gray-800 text-gray-300 mt-auto">
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* About section */}
          <div>
            <h3 className="text-white font-bold text-lg mb-4">{t('common:footer.aboutTitle')}</h3>
            <p className="text-sm leading-relaxed mb-4">
              {t('common:footer.aboutDescription')}
            </p>
            <Link to={localizedPath('/')} className="flex items-center space-x-2 hover:opacity-80 transition-opacity">
              <img
                src="/vpkhcn-logo.png"
                alt="VASTP Logo"
                className="w-10 h-10 object-contain rounded-full"
              />
              <span className="text-xs font-medium">{t('common:site.title')}</span>
            </Link>
          </div>

          {/* Quick links */}
          <div>
            <h3 className="text-white font-bold text-lg mb-4">{t('common:footer.quickLinksTitle')}</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to={localizedPath('/')} className="hover:text-primary-400 transition-colors">
                  {t('navigation:menu.home')}
                </Link>
              </li>
              <li>
                <Link to={localizedPath('/news')} className="hover:text-primary-400 transition-colors">
                  {t('navigation:menu.news')}
                </Link>
              </li>
              <li>
                <Link to={localizedPath('/conferences')} className="hover:text-primary-400 transition-colors">
                  {t('navigation:menu.conferences')}
                </Link>
              </li>
              <li>
                <Link to={localizedPath('/about')} className="hover:text-primary-400 transition-colors">
                  {t('navigation:menu.about')}
                </Link>
              </li>
              <li>
                <a href={localizedPath('/about') + '#contact'} className="hover:text-primary-400 transition-colors">
                  {t('navigation:menu.contact')}
                </a>
              </li>
            </ul>
          </div>

          {/* Contact and social */}
          <div>
            <Link to={localizedPath('/about') + '#contact'} className="text-white font-bold text-lg mb-4 block hover:text-primary-400 transition-colors">
              {t('common:footer.connectTitle')}
            </Link>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start">
                <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <a href="mailto:hoikhcnvietnamtaibalan@gmail.com" className="hover:text-primary-400 transition-colors">
                  hoikhcnvietnamtaibalan@gmail.com
                </a>
              </li>
              <li className="flex items-start">
                <svg className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>{t('common:footer.location')}</span>
              </li>
            </ul>

            {/* Social media links */}
            <div className="mt-4">
              <p className="text-sm font-medium mb-2">{t('common:footer.followUs')}</p>
              <div className="flex space-x-3">
                <a
                  href="https://www.facebook.com/khcn.vietnam/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center hover:bg-primary-600 transition-colors"
                  aria-label="Facebook"
                  title="Facebook Page"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                  </svg>
                </a>
                <a
                  href="https://www.facebook.com/groups/khcn.vietnam"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 bg-gray-700 rounded-full flex items-center justify-center hover:bg-primary-600 transition-colors"
                  aria-label="Facebook Group"
                  title="Facebook Group"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-gray-700 mt-8 pt-6 text-center text-sm">
          <p>
            © {currentYear} {t('common:footer.copyright')}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
